import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertCircle,
  ArrowRight,
  Lock,
  Mail,
  Eye,
  EyeOff,
} from 'lucide-react';

const Login: React.FC = () => {
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForgotNote, setShowForgotNote] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      setError((err as Error).message || 'Invalid credentials or network failure');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign In">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Field */}
        <div className="space-y-1.5">
          <label htmlFor="email" className="text-xs font-semibold text-foreground">
            Email
          </label>
          <Input
            id="email"
            type="text"
            icon={<Mail className="h-4 w-4" />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com or username"
            required
            autoComplete="username"
          />
        </div>

        {/* Password Field */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="text-xs font-semibold text-foreground">
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowForgotNote(!showForgotNote)}
              className="text-[11px] font-medium text-primary hover:underline transition-colors"
            >
              Forgot password?
            </button>
          </div>
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            icon={<Lock className="h-4 w-4" />}
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded focus:outline-none"
                aria-label={showPassword ? 'Hide input' : 'Show input'}
                title={showPassword ? 'Hide input' : 'Show input'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />
        </div>

        {/* Forgot password note */}
        {showForgotNote && (
          <div className="rounded-lg border border-primary/25 bg-primary/10 p-2.5 text-xs text-muted-foreground animate-fade-in">
            Contact your system administrator to reset your password.
          </div>
        )}

        {/* Remember me */}
        <div className="flex items-center justify-between py-0.5">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-teal-700 text-primary focus:ring-primary"
            />
            <span className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Remember me
            </span>
          </label>
        </div>

        {/* Error Notification */}
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive-soft p-2.5 text-xs text-destructive animate-fade-in">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="font-medium">{error}</p>
          </div>
        )}

        {/* Primary Submit Button */}
        <Button
          type="submit"
          className="w-full bg-primary hover:bg-primary-hover text-primary-foreground font-semibold shadow-md transition-all active:scale-[0.99]"
          loading={loading}
          size="lg"
        >
          {loading ? 'Signing in…' : 'Sign In'}
          {!loading && <ArrowRight className="h-4 w-4 ml-1" />}
        </Button>

        {/* Registration is closed: accounts are created by the admin. */}
        <div className="pt-2 text-center text-xs text-muted-foreground">
          Registration is closed — ask an administrator to create your account.
        </div>
      </form>
    </AuthShell>
  );
};

export default Login;
